/**
 * CHIP mode (spec §5.3): every `intervalSeconds / chipRateMult` seconds of beam contact an ore chunk breaks
 * off. Its ware is drawn from the rock's mix, its mass from `chip.chunkMassT` × the yield multiplier (fragments of
 * a fractured rock chip `fragmentYieldMult` more), its eject speed from `chip.ejectSpeed`. The random draws use
 * the caller's seeded `Rng` (`space.rng`, never `sim.rng`). Allocation-free (D-014).
 */
import { WARE_COUNT, type MiningConfig } from '../data/m2Schemas'
import { nextFloat, type Rng } from '../core/rng'

export interface ChipState {
  /** Seconds of contact accumulated toward the next chunk (the readout's `chipTimer`). */
  timer: number
}

export function createChipState(): ChipState {
  return { timer: 0 }
}

/** Restart the chunk timer (new rock, beam released). */
export function resetChip(state: ChipState): void {
  state.timer = 0
}

/** The chunk `stepChip` broke off (caller-owned). */
export interface ChunkSpawn {
  /** Ware index. */
  ware: number
  /** Tonnes before the runtime clamps it to the rock's remaining mass. */
  massT: number
  /** Speed off the surface, m/s. */
  speed: number
}

export function createChunkSpawn(): ChunkSpawn {
  return { ware: 0, massT: 0, speed: 0 }
}

/** Seconds of contact per chunk at `chipRateMult`. */
export function chipInterval(chipRateMult: number, cfg: Pick<MiningConfig, 'chip'>): number {
  return cfg.chip.intervalSeconds / chipRateMult
}

/** Draws a ware index from `mix` with the uniform `u` in [0, 1) (the last ore present absorbs round-off). */
export function drawWare(mix: Float32Array, u: number): number {
  let acc = 0
  let last = 0
  for (let w = 0; w < WARE_COUNT; w++) {
    if (mix[w] <= 0) continue
    acc += mix[w]
    last = w
    if (u < acc) return w
  }
  return last
}

/**
 * Adds `dt` of beam contact. Returns true when a chunk breaks off this tick and writes it to `out`
 * (at most one per call; the leftover time carries to the next chunk).
 */
export function stepChip(
  state: ChipState,
  dt: number,
  chipRateMult: number,
  yieldMult: number,
  mix: Float32Array,
  cfg: Pick<MiningConfig, 'chip'>,
  rng: Rng,
  out: ChunkSpawn,
): boolean {
  const interval = cfg.chip.intervalSeconds / chipRateMult
  state.timer += dt
  if (state.timer < interval) return false
  state.timer -= interval
  if (state.timer > interval) state.timer = interval // never bank more than one chunk
  const c = cfg.chip
  out.ware = drawWare(mix, nextFloat(rng))
  out.massT = (c.chunkMassT[0] + (c.chunkMassT[1] - c.chunkMassT[0]) * nextFloat(rng)) * yieldMult
  out.speed = c.ejectSpeed[0] + (c.ejectSpeed[1] - c.ejectSpeed[0]) * nextFloat(rng)
  return true
}
